import { useState } from 'react'

import { Box, SearchField, matchText } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import i18n from 'modules/core/lib/i18n'

import EmployeesSection from 'modules/shift_management/components/ShiftManagement/EmployeesSection'
import TimePeriodSelector from 'modules/shift_management/components/ShiftManagement/TimePeriodSelector'
import { ShiftInputsAttributesByDate } from 'modules/shift_management/hooks/useDefaultAttributesFromRows'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import type CommonSelectorsForm from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import type DefaultShiftWithParamsForm from 'modules/shift_management/lib/forms/DefaultShiftWithParamsForm'
import { Leave } from 'modules/shift_management/lib/types'

import AddNewShift from './AddNewShift'
import SavedShiftsList from './SavedShiftsList'
import { FROM } from './utils'

interface Props {
  forms: DefaultShiftWithParamsForm[]
  defaultAttributes: ShiftInputsAttributesByDate
  commonSelectorsForm: CommonSelectorsForm
  selectableEmployeeIds: string[]
  onToggleShift: (id: string) => void
  handleChangeTab?: () => void
  canReassignShifts?: boolean
  edit?: boolean
  showError?: boolean
  setTrackSearch?: React.Dispatch<React.SetStateAction<string>>
  leave?: Leave
}

const SavedShiftsSection = ({
  forms,
  defaultAttributes,
  commonSelectorsForm,
  selectableEmployeeIds,
  onToggleShift,
  handleChangeTab,
  canReassignShifts = false,
  edit = false,
  showError = false,
  setTrackSearch,
  leave,
}: Props) => {
  const [{ view }] = useSchedule()
  const noSavedShifts = !forms.length
  const selectedEmployees = commonSelectorsForm.employeeSelection?.ids ?? []

  const { isEnabled: hasSelection } = useSelectionState()

  const [{ canManageDefaultShifts }] = usePermissions()
  const defaultShiftIds = forms.map((form) => form.get('id').value)

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value || '')
    setTrackSearch && setTrackSearch(event.target.value)
  }
  const shiftIds = new Set(defaultShiftIds)
  const [search, setSearch] = useState('')
  const filteredForms = forms.filter((ds) =>
    matchText(search, ds.get('title').value || shiftIds.has(ds.get('id').value))
  )

  const showTimeSelector = !hasSelection || (!!leave && view.isWeekly)

  return (
    <>
      {noSavedShifts ? (
        <Box gap="s24">
          <Box gap="s16">
            <EmployeesSection
              form={commonSelectorsForm}
              selectableEmployeeIds={selectableEmployeeIds}
              selectedEmployees={selectedEmployees}
              canReassignShifts={canReassignShifts}
              edit={edit}
              fromLeave={!!leave}
            />
            {showTimeSelector && <TimePeriodSelector form={commonSelectorsForm} leave={leave} />}
          </Box>

          {handleChangeTab && (
            <AddNewShift
              handleChangeTab={handleChangeTab}
              from={FROM.NO_SHIFTS}
              canManageDefaultShifts={!!canManageDefaultShifts}
            />
          )}
        </Box>
      ) : (
        <Box gap="s24">
          <Box gap="s16">
            <EmployeesSection
              form={commonSelectorsForm}
              selectableEmployeeIds={selectableEmployeeIds}
              selectedEmployees={selectedEmployees}
              canReassignShifts={canReassignShifts}
              edit={edit}
              fromLeave={!!leave}
            />
            {showTimeSelector && <TimePeriodSelector form={commonSelectorsForm} leave={leave} />}
          </Box>
          <Box gap="s8">
            <SearchField
              placeholder={i18n.t('shift_management.add_shift.search_by_title')}
              onSearch={onSearch}
              search={search}
            />
            {showError && (
              <Box className="error-anchor">
                <Callout
                  type="critical"
                  title={i18n.t('shift_management.actions_sidepanel.errors.no_saved_shift.title')}
                  description={i18n.t(
                    'shift_management.actions_sidepanel.errors.no_saved_shift.description'
                  )}
                />
              </Box>
            )}
            <SavedShiftsList
              forms={filteredForms}
              defaultAttributes={defaultAttributes}
              onToggleShift={onToggleShift}
            />

            {handleChangeTab && canManageDefaultShifts && (
              <AddNewShift
                handleChangeTab={handleChangeTab}
                from={FROM.NEW_SHIFT}
                canManageDefaultShifts={!!canManageDefaultShifts}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  )
}

export default observer(SavedShiftsSection)
